import { useState, useEffect, useMemo } from "react";
import styles from "./financeiro.module.css";
import ui from "../../styles/ui.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../services/api";
import { FiTrash2, FiEdit, FiX, FiPlus } from "react-icons/fi";
import { MdCleaningServices } from "react-icons/md";
import { CgCalculator } from "react-icons/cg";
import Spinner from "../../components/Spinner/carregando";
import Calculadora from "../../components/Calculadora/index.jsx";
import useConfirmDialog from "../../components/ConfirmModal/useConfirmDialog.jsx";


function Financeiro() {
  // ─── 1. Estados principais ───────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [transacoes, setTransacoes] = useState([]);
  const [taxas, setTaxas] = useState([]); // Para possíveis conversões
  const [itemMaisRecente, setItemMaisRecente] = useState(null);

  // Controle de visibilidade de formulário e filtros
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Formulário de cadastro/edição:
  const [formData, setFormData] = useState({
    ID: null, // se for edição, terá ID; se null, será novo
    idCliente: "",
    nome: "",
    valor: "",
    moeda: "BRL",
    data: "",
    obs: "",
    tipo: "Me deve",
    status: "A PAGAR",
  });

  // Sugestões de cliente (autocomplete)
  const [sugestoes, setSugestoes] = useState([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const { confirm, ConfirmModal } = useConfirmDialog({
    confirmClassName: `${ui.butdesktop} ${ui.btnPrimary}`,
    cancelClassName: ui.butdesktop,
  });
  const [sugestaoSelecionada, setSugestaoSelecionada] = useState(-1);

  // ─── 2. Filtros de exibição ────────────────────────────────────────────
  const [filterNome, setFilterNome] = useState("");
  const [filterTipo, setFilterTipo] = useState("Me deve"); // default pré-selecionado
  // Por padrão, sem filtro de Status; o usuário pode selecionar depois
  const [filterStatus, setFilterStatus] = useState("A PAGAR"); // default pré-selecionado
  const [filterMoeda, setFilterMoeda] = useState("");
  const [filterDataInicio, setFilterDataInicio] = useState("");
  const [filterDataFim, setFilterDataFim] = useState("");

  // Mensagem de feedback (sucesso/erro)
  const [message, setMessage] = useState("");
  const [showCalculadora, setShowCalculadora] = useState(false);

  // ─── 3. Carregar dados do servidor (transações + taxas) ────────────────
  const carregarDados = async () => {
    try {
      setIsLoading(true);
      // 3.1) BUSCAR TRANSAÇÕES
      //   - Se houver período definido, envia ao backend
      //   - Caso contrário, não envia params (backend retorna tudo por padrão)
      const resFinanceiro = (filterDataInicio || filterDataFim)
        ? await api.get("/financeiro", { params: { dataInicio: filterDataInicio || undefined, dataFim: filterDataFim || undefined } })
        : await api.get("/financeiro");
      // 3.2) BUSCAR TAXAS (sem parâmetros de data)
      const resTaxas = await api.get("/consultas-saldos/taxas");

      // Encontra a taxa mais recente, por data
      if (Array.isArray(resTaxas.data) && resTaxas.data.length > 0) {
        const maisRecente = resTaxas.data.reduce((prev, curr) => {
          return new Date(curr.DATA) > new Date(prev.DATA) ? curr : prev;
        }, resTaxas.data[0]);
        setItemMaisRecente(maisRecente);
        setTaxas(resTaxas.data);
      } else {
        setItemMaisRecente(null);
        setTaxas([]);
      }

      // Mostramos os registros retornados pelo backend
      const items = Array.isArray(resFinanceiro.data) ? resFinanceiro.data : [];
      setTransacoes(items);
      setMessage("");
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Não foi possível carregar os dados do servidor.");
    } finally {
      setIsLoading(false);
    }
  };

  // Carrega sempre que o período mudar (inclusive vazio = tudo)
  useEffect(() => {
    carregarDados();
  }, [filterDataInicio, filterDataFim]);

  // ─── 4. Manipulação do formulário de adicionar/editar ─────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Se o campo modificado for "nome", buscamos sugestões de cliente
    if (name === "nome") {
      if (value.trim() !== "") {
        api
          .get("/clientes")
          .then((res) => {
            const sugestoesFiltradas = res.data
              .filter((cliente) =>
                cliente.NOME.toLowerCase().includes(value.toLowerCase())
              )
              .map((cliente) => ({ id: cliente.ID, nome: cliente.NOME }));
            setSugestoes(sugestoesFiltradas);
            setMostrarSugestoes(sugestoesFiltradas.length > 0);
          })
          .catch((erro) => {
            toast.error("Erro ao buscar sugestões de clientes");
            console.error("Erro ao buscar sugestões:", erro);
          });
      } else {
        setSugestoes([]);
        setMostrarSugestoes(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      // Normaliza sinal do valor conforme o tipo selecionado
      const valorNum = parseFloat(String(formData.valor).replace(',', '.')) || 0;
      const valorSigned = formData.tipo === 'Devo' ? -Math.abs(valorNum) : Math.abs(valorNum);
      const payload = { ...formData, valor: valorSigned };
      if (formData.ID) {
        // Se tiver ID, é edição
        await api.put(`/financeiro/${formData.ID}`, payload);
        toast.success("Lançamento atualizado com sucesso!");
      } else {
        // Cadastro novo
        await api.post("/financeiro", payload);
        toast.success("Lançamento gravado com sucesso!");
      }
      // Depois que criar/editar, reseta o form e recarrega a lista
      setFormData({
        ID: null,
        idCliente: "",
        nome: "",
        valor: "",
        moeda: "BRL",
        data: "",
        obs: "",
        tipo: "Me deve",
        status: "A PAGAR",
      });
      carregarDados();
    } catch (error) {
      console.error("Erro ao gravar/atualizar:", error);
      toast.error("Erro ao gravar/atualizar!");
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir/fechar calculadora e aplicar resultado no campo Valor
  const abrirCalculadora = () => setShowCalculadora(true);
  const aoFecharCalculadora = (resultado) => {
    if (resultado !== null && resultado !== undefined) {
      setFormData((prev) => ({ ...prev, valor: String(resultado) }));
    }
    setShowCalculadora(false);
  };

  const onEditFinan = (ID) => {
    // Encontra a transação na lista e popula o formData
    const transacao = transacoes.find((t) => t.ID === ID);
    if (transacao) {
      setFormData({
        ID: transacao.ID,
        idCliente: transacao.ID_CLIENTE || "",
        nome: transacao.NOME || "",
        valor: transacao.VALOR || "",
        moeda: transacao.MOEDA || "BRL",
        data: transacao.DATA_FORMATADA || "",
        obs: transacao.OBS || "",
        tipo: transacao.TIPO || "Me deve",
        status: transacao.STATUS || "A PAGAR",
      });
      setShowForm(true); // abre o formulário em modo edição
      toast.warn("Modo edição ativado");
    } else {
      toast.error("Erro ao carregar dados para edição");
    }
  };

  const onDeleteFinan = async (ID) => {
    try {
      const confirmacao = await confirm("Deseja deletar esse lançamento?");
      if (!confirmacao) {
        toast.warn("Exclusão cancelada!");
        return;
      }
      setIsLoading(true);
      const res = await api.delete(`/financeiro/${ID}`);
      const ok = res?.data?.success === true || res?.data?.ok === true;
      if (ok) {
        toast.success("Deletado com sucesso!");
        carregarDados();
      } else {
        toast.error(res?.data?.message || "Falha ao deletar");
      }
    } catch (error) {
      console.error("Erro ao deletar!", error);
      toast.error(`Erro ao deletar: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── 5. Manipulação de sugestões de cliente ────────────────────────────
  function toggleFormulario() {
    setShowForm(false);
  }
  
  const handleKeyDown = (e) => {
    if (mostrarSugestoes && sugestoes.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSugestaoSelecionada((prev) =>
          prev < sugestoes.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSugestaoSelecionada((prev) =>
          prev > 0 ? prev - 1 : sugestoes.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (sugestaoSelecionada >= 0) {
          handleSugestaoClick(sugestoes[sugestaoSelecionada]);
        }
      } else if (e.key === "Escape") {
        setMostrarSugestoes(false);
      }
    }
  };

  const handleSugestaoClick = (selecionado) => {
    setFormData((prev) => ({
      ...prev,
      nome: selecionado.nome,
      idCliente: selecionado.id,
    }));
    setMostrarSugestoes(false);
    setSugestaoSelecionada(-1);
  };

  const selecionarCliente = (cliente) => {
    setFormData((prev) => ({
      ...prev,
      nome: cliente.nome,
      idCliente: cliente.id,
    }));
    setMostrarSugestoes(false);
    setSugestaoSelecionada(-1);
  };

  // ─── 6. Cálculo de transações filtradas (useMemo para performance) ──────
  const transacoesFiltradas = useMemo(() => {
    const nomeFiltro = (filterNome || '').toLowerCase();
    const tipoFiltro = (filterTipo || '').toUpperCase();
    const statusFiltro = (filterStatus || '').toUpperCase();
    const moedaFiltro = (filterMoeda || '').toUpperCase();
    const di = filterDataInicio || '';
    const df = filterDataFim || '';

    return (Array.isArray(transacoes) ? transacoes : []).filter((t) => {
      const nome = String(t.NOME || '').toLowerCase();
      const tipo = String(t.TIPO || '').toUpperCase();
      const status = String(t.STATUS || '').toUpperCase();
      const moeda = String(t.MOEDA || '').toUpperCase();
      const dataFmt = String(t.DATA_FORMATADA || t.DATA || t.DATA_ORIGINAL || '');

      if (nomeFiltro && !nome.includes(nomeFiltro)) return false;
      if (tipoFiltro && tipo !== tipoFiltro) return false;
      if (statusFiltro && status !== statusFiltro) return false;
      if (moedaFiltro && moeda !== moedaFiltro) return false;
      if (di && dataFmt < di) return false;
      if (df && dataFmt > df) return false;
      return true;
    });
  }, [transacoes, filterNome, filterTipo, filterStatus, filterMoeda, filterDataInicio, filterDataFim]);

  // ─── 7. Cálculo do total (soma simples dos VALOR filtrados) ────────────
  const totalFiltrado = useMemo(() => {
    if (!transacoesFiltradas.length) return "0.00";
    const soma = transacoesFiltradas.reduce((acc, t) => {
      const v = parseFloat(t.VALOR) || 0;
      return acc + v;
    }, 0);
    return soma.toFixed(2);
  }, [transacoesFiltradas]);

  const fmtValor = (v) => {
    const num = Number(v);
    return Number.isFinite(num) ? num.toFixed(2) : "-";
  };

  const fmtData = (s) => {
    if (!s) return "-";
    const date = new Date(`${s}T00:00:00`);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("pt-BR");
  };

  // ─── 8. Renderização final ──────────────────────────────────────────────
  return (
    <main className={`${styles["financeiro-container"]} ${styles.root}`}>
      <ConfirmModal />
      {/* ── TOGGLES (Switches iPhone) ── */}
      

      {/* ── Formulário ── */}
      {showForm && (
        <div className={`${ui.divnegratelatotal} ${styles.modalOverlay}`}>
          
        <form onSubmit={handleSubmit} className={`${styles["form-container"]} ${styles.modalCard}`}>
          <button onClick={toggleFormulario} className={`${ui.butfechar}`}>
            <FiX size={24} />
          </button>
          <h3 className={`${ui.titp}`}>Lançamento Financeiro</h3>
          {/* Linha com Nome + Sugestão de Cliente */}
          <div className={styles["finan-nomevalormoeda"]}>
            <div className={styles["finan-nome"]}>
              <label className={`${ui.labdesktop}`} htmlFor="nome">Nome (Cliente)</label>
              <input
                className={`${ui.inpdesktop}`}
                type="text"
                placeholder="Nome..."
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
                autoComplete="off"
              />
              {mostrarSugestoes && sugestoes.length > 0 && (
                <ul className={styles["finan-sugestoes"]}>
                  {sugestoes.map((cliente, index) => (
                    <li
                      key={cliente.id}
                      className={`${styles["finan-sugestao-item"]} ${index === sugestaoSelecionada ? styles.selecionada : ""}`}
                      onClick={() => selecionarCliente(cliente)}
                    >
                      {cliente.nome} (ID: {cliente.id})
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Valor e Moeda */}
            <div className={styles["finan-valoremoeda"]}>
              <div style={{ position: "relative" }}>
                <label className={`${ui.labdesktop}`} htmlFor="valor">Valor</label>
                <input
                  className={`${ui.inpdesktop}`}
                  type="text"
                  inputMode="decimal"
                  placeholder="Valor"
                  id="valor"
                  name="valor"
                  value={formData.valor}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  onClick={abrirCalculadora}
                  className={`${ui.calcbtn}`}
                  aria-label="Abrir calculadora para Valor"
                >
                  <CgCalculator size={20} />
                </button>
              </div>
              <div>
                <label className={`${ui.labdesktop}`} htmlFor="moeda">Moeda</label>
                <select
                  className={`${ui.seldesktop}`}
                  id="moeda"
                  name="moeda"
                  value={formData.moeda}
                  onChange={handleChange}
                  required
                >
                  <option value="USD">USD (Dólar)</option>
                  <option value="BRL">BRL (Real)</option>
                  <option value="BOB">BOB (Boliviano)</option>
                </select>
              </div>
              <div className={styles["data"]}>
                <label className={`${ui.labdesktop}`} htmlFor="data">Data:</label>
                <input
                  className={`${ui.inpdesktop}`}
                  type="date"
                  id="data"
                  name="data"
                  value={formData.data}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          {/* Tipo, Status e OBS */}
          <div className={styles["finan-dataobstipostatus"]}>
            <div className={styles["tipostatus"]}>
              <div className={styles["obs"]}>
                <label className={`${ui.labdesktop}`} htmlFor="obs">OBS:</label>
                <input
                  className={`${ui.inpdesktop}`}
                  type="text"
                  id="obs"
                  name="obs"
                  value={formData.obs}
                  onChange={handleChange}
                  placeholder="Observações (opcional)"
                />
              </div>
              <div className={styles["tipo"]}>
                <label className={`${ui.labdesktop}`} htmlFor="tipo">Tipo:</label>
                <select
                  className={`${ui.seldesktop}`}
                  id="tipo"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  required
                >
                  <option value="Devo">Devo</option>
                  <option value="Me deve">Me deve</option>
                </select>
              </div>
              <div className={styles["status"]}>
                <label className={`${ui.labdesktop}`} htmlFor="status">Status:</label>
                <select
                  className={`${ui.seldesktop}`}
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="PAGO">Pago</option>
                  <option value="A PAGAR">A Pagar</option>
                </select>
              </div>
              
              <div className={styles["enviar"]}>
                <label className={`${ui.labtransparente}`} htmlFor="btn">...</label>
                <button className={`${ui.butdesktop}`} type="submit">
                  {formData.ID ? "Atualizar" : "Salvar"}
                </button>
              </div>
            </div>
          </div>
        </form>
        </div>
      )}

      {/* ── Barra de Filtros ── */}
      {showFilters && (
        <div className={styles["filtros-container"]}>
          {/* Filtro por Nome */}
          <div className={styles.filterNome}>
            <label>Nome:</label>
            <input
              className={`${ui.inpdesktop}`}
              type="text"
              placeholder="Buscar por nome"
              value={filterNome}
              onChange={(e) => setFilterNome(e.target.value)}
            />
          </div>

          {/* Filtro por Tipo */}
          <div className={styles.filterTipo}>
            <label>Tipo:</label>
            <select
              className={`${ui.seldesktop}`}
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
            >
              <option value="">– Todos –</option>
              <option value="Devo">Devo</option>
              <option value="Me deve">Me deve</option>
            </select>
          </div>

          {/* Filtro por Status */}
          <div className={styles.filterStatus}>
            <label>Status:</label>
            <select
              className={`${ui.seldesktop}`}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">– Todos –</option>
              <option value="PAGO">Pago</option>
              <option value="A PAGAR">A Pagar</option>
            </select>
          </div>

          {/* Filtro por Moeda */}
          <div className={styles.filterMoeda}>
            <label>Moeda:</label>
            <select
              className={`${ui.seldesktop}`}
              value={filterMoeda}
              onChange={(e) => setFilterMoeda(e.target.value)}
            >
              <option value="">– Todas –</option>
              <option value="USD">USD</option>
              <option value="BRL">BRL</option>
              <option value="BOB">BOB</option>
            </select>
          </div>

          {/* Filtro por Data Início */}
          <div className={styles.filterDataInicio}>
            <label>Data Início:</label>
            <input
              className={`${ui.inpdesktop}`}
              type="date"
              value={filterDataInicio}
              onChange={(e) => setFilterDataInicio(e.target.value)}
            />
          </div>

          {/* Filtro por Data Fim */}
          <div className={styles.filterDataFim}>
            <label>Data Fim:</label>
            <input
              className={`${ui.inpdesktop}`}
              type="date"
              value={filterDataFim}
              onChange={(e) => setFilterDataFim(e.target.value)}
            />
          </div>

          {/* Botão “Limpar Filtros” */}
              <div className={styles.filterClear}>
                <button
                  className={`${ui.butdesktop} ${ui.btnSecondary}`}
                  type="button"
                  aria-label="Limpar filtros"
                  onClick={() => {
                setFilterNome("");
                setFilterTipo("");
                setFilterStatus("");
                setFilterMoeda("");
                setFilterDataInicio("");
                setFilterDataFim("");
                  }}
                >
                  <MdCleaningServices aria-hidden="true" />
                </button>
              </div>
        </div>
      )}

      

      {/* ── Tabela de Lançamentos ── */}
      <div className={styles["finan-div-tabela"]}>
        <div className={styles.tableWrap}>
          <table className={ui.venda_tabela}>
            <thead>
              <tr>
                <th title="Nome" >Nome</th>
                <th title="Valor">Valor</th>
                <th title="Moeda">Moeda</th>
                <th title="Data">Data</th>
                <th title="Descrição">Descrição</th>
                <th title="Tipo">Tipo</th>
                <th title="Status">Status</th>
                <th title="Editar ou Excluir">Ações</th>
              </tr>
            </thead>
            <tbody>
            {transacoesFiltradas.map((t) => {
              const dataStr = t.DATA_FORMATADA || t.DATA || t.DATA_ORIGINAL;
              return (
                <tr key={t.ID}>
                  <td title={t.NOME} className={ui.col_nomebrl}>{t.NOME}</td>
                  <td title={fmtValor(t.VALOR)} className={ui.col_valorbrl}>{fmtValor(t.VALOR)}</td>
                  <td title={t.MOEDA} className={ui.col_valorbob}>{t.MOEDA}</td>
                  <td title={fmtData(dataStr)}>{fmtData(dataStr)}</td>
                  <td title={t.OBS} className={ui.col_nomebrl}>{t.OBS} </td>
                  <td title={t.TIPO} className={ui.col_nomebrl}>{t.TIPO}</td>
                  <td title={t.STATUS} className={ui.col_nomebob}>{t.STATUS}</td>
                  <td title="Editar ou Excluir" className={`${ui.buteditarremover}`}>
                    <button
                      title="Editar"
                      className={`${ui.buteditar}`}
                      onClick={() => onEditFinan(t.ID)}
                    >
                      <FiEdit />
                    </button>
                    <button
                      title="Excluir"
                      className={`${ui.butremover}`}
                      onClick={() => onDeleteFinan(t.ID)}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              );
            })}
            </tbody>
            <tfoot>
              <tr>
                <td >Registros ({transacoesFiltradas.length})</td>
                {/*<td colSpan="1">Total ({filterMoeda || "Todas Moedas"}):</td>*/}
                <td>{totalFiltrado}</td>
                <td>{filterMoeda || "-"}</td>
                <td colSpan="5"></td>
                
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className={`${ui.floatingFab}`}>
        <button
          className={`${ui.novaBtn} ${ui.addBtn}`}
          onClick={() => setShowForm(true)}
          aria-label="Adicionar novo lançamento"
        >
          <FiPlus />
        </button>
      </div>
      <ToastContainer limit={1} position="top-right" autoClose={3000} />
      {showCalculadora && (
        <div className={`${ui.divnegratelatotal}`}>
          <Calculadora onClose={aoFecharCalculadora} />
        </div>
      )}
      <Spinner isLoading={isLoading} />
    </main>
  );
}

export default Financeiro;
